// A physical connection's pipe in the 3D view: a tube along its centreline (its
// elbows sampled), hollow at its ends.
import * as THREE from 'three';
import type { Vec3 } from '../pipes';

const SIDES = 20;

/** A tube of outer radius `r` (inner `ri`) along `pts` (drawing mm: x, y in
 *  plan, z up). Each ring is turned along with the centreline (parallel
 *  transport), so the tube never twists; at a corner with no elbow it is mitred
 *  (the ring widened by the half-angle). Its two ends are rings, the bore open,
 *  square to its first and last runs, or to `ends` (unit directions, a smooth
 *  curve's own at its ends) when given. */
export function tubeMesh(pts: readonly Vec3[], r: number, ri: number, material: THREE.Material, ends?: readonly [Vec3, Vec3]): THREE.Mesh | null {
  const P = pts.map((p) => new THREE.Vector3(p.x, p.z, -p.y)).filter((p, i, a) => i === 0 || p.distanceTo(a[i - 1]) > 1e-6);
  if (P.length < 2 || r <= 0) return null;
  const n = P.length;
  const seg = P.slice(1).map((p, i) => p.clone().sub(P[i]).normalize());
  const endDir = (e: Vec3): THREE.Vector3 => new THREE.Vector3(e.x, e.z, -e.y).normalize();
  const tan = P.map((_, i) => (i === 0 ? (ends ? endDir(ends[0]) : seg[0]) : i === n - 1 ? (ends ? endDir(ends[1]) : seg[n - 2]) : seg[i - 1].clone().add(seg[i]).normalize()));
  // A first normal square to the first run, then carried along.
  const up = Math.abs(tan[0].y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  let nrm = up.clone().sub(tan[0].clone().multiplyScalar(up.dot(tan[0]))).normalize();
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < n; i++) {
    if (i > 0) {
      const q = new THREE.Quaternion().setFromUnitVectors(tan[i - 1], tan[i]);
      nrm = nrm.applyQuaternion(q).normalize();
    }
    const bin = tan[i].clone().cross(nrm).normalize();
    // At a corner the ring is widened across the turn (the direction `m` in its
    // plane), as a mitre is.
    const k = i === 0 || i === n - 1 ? 1 : 1 / Math.max(0.3, seg[i - 1].dot(tan[i]));
    const m = i === 0 || i === n - 1 ? null : seg[i - 1].clone().sub(tan[i].clone().multiplyScalar(seg[i - 1].dot(tan[i])));
    if (m && m.length() > 1e-9) m.normalize();
    for (let s = 0; s < SIDES; s++) {
      const a = (s / SIDES) * Math.PI * 2;
      const off = nrm.clone().multiplyScalar(Math.cos(a) * r).add(bin.clone().multiplyScalar(Math.sin(a) * r));
      if (m && m.length() > 0.5) off.add(m.clone().multiplyScalar(off.dot(m) * (k - 1)));
      const v = P[i].clone().add(off);
      pos.push(v.x, v.y, v.z);
    }
  }
  for (let i = 0; i < n - 1; i++) {
    for (let s = 0; s < SIDES; s++) {
      const a = i * SIDES + s, b = i * SIDES + ((s + 1) % SIDES), c = (i + 1) * SIDES + s, d = (i + 1) * SIDES + ((s + 1) % SIDES);
      idx.push(a, c, b, b, c, d);
    }
  }
  // The end rings: from the outer wall to the bore.
  for (const end of [0, n - 1]) {
    const o = P[end];
    const base = pos.length / 3;
    for (let s = 0; s < SIDES; s++) {
      const x = pos[(end * SIDES + s) * 3], y = pos[(end * SIDES + s) * 3 + 1], z = pos[(end * SIDES + s) * 3 + 2];
      const v = new THREE.Vector3(x, y, z).sub(o).multiplyScalar(Math.max(0, Math.min(1, ri / r))).add(o);
      pos.push(x, y, z, v.x, v.y, v.z);
    }
    for (let s = 0; s < SIDES; s++) {
      const a = base + s * 2, b = base + ((s + 1) % SIDES) * 2;
      if (end === 0) idx.push(a, a + 1, b, b, a + 1, b + 1);
      else idx.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return new THREE.Mesh(g, material);
}
